import React from 'react';
import Select from "./../../common/Select.jsx";

import { Selection } from '../form_components.jsx';
import Setting from './setting.jsx';
import {Mode, DataType} from '../common.jsx';
import { fetchJson } from '../../../helpers/fetch.js';
import {FormContext} from '../form_context.jsx';

import { datagrid_get_row_path, datagrid_get_cells_for_column_path } from '~/routes';

const getSelectedOption = (options, value) => options.find(opt => opt.value?.id && opt.value.id == value?.id);

// Limit preset options to only the ones that don't do any grouping (or total aggregation)
const nonGroupedFilter = (preset) => Object.values(preset.columns).every(col => col.aggFunc == null && !col.rowGroup);

export default class DatagridRowLookup extends Selection {
  static contextType = FormContext;

  static get TYPE() {
    return 'adalia_me_datagrid_row_lookup';
  }

  static get CATEGORY() {
    return 'datagrid';
  }

  static icon() {
    return <i className='fa fa-magnifying-glass'></i>;
  }

  static get TRANSLATED_ATTRIBUTES() {
    return {
      label: 'component_title_adalia_me_datagrid_row_updater',
      description: 'field_default_description',
      help: null,
      blankOptionLabel: 'select_blank_option_default'
    };
  }

  static defaultContent(language) {
    return Object.assign(super.defaultContent(language), {
      deletable: true,
      cloneable: false,
      allowMultiple: false,
      dataType: DataType.STRING,
      includeBlank: true,
      blankOptionLabel: 'blankOptionLabel'
    });
  }

  constructor(props){
    super(props);
    // this.selectClicked = this.selectClicked.bind(this);
    // this.selectRef = React.createRef();
    this.state = {
      ...this.state,
      options: [],
      loading: true,
    }
  }

  componentDidMount() {
    super.componentDidMount();
    this.setState({loading: false});

    
    if (this.props.mode == Mode.FILL){
      const projectId = this.context.projectId;
      const {datagridId, presetId, columnAccessor} = this.props.content;

      if (projectId && columnAccessor) {
        this.setState({loading: true});

        fetchOptionsForSelect(
          projectId,
          datagridId,
          presetId,
          columnAccessor,
          (res) => {
              if (res) this.setState({options: res, loading: false})
          }
        );
      }
    }
    else if (this.props.mode == Mode.VIEW){
      // Answers contains the selected option
      const selectedOption = this.getAnswer()?.selectedOption;
      if (selectedOption) this.setState({options: [{value: this.getAnswer(), label: selectedOption}]})
    }
  }

  getSelected() {
    return [Mode.FILL, Mode.VIEW].includes(this.props.mode) ? this.getAnswer() : [];
  }

  // Only single allowed
  getSelectedOptions(_answer) {
    return getSelectedOption(this.state.options, this.getAnswer());
  }

  getFormattedAnswer() {
    return this.getAnswer();
  }

  getAnswer() {
    // When received as an initial prop, i.e. answer exists. It's stored as string due to uncontrolled form
    if (typeof this.getRawAnswer() == 'string' && this.getRawAnswer() != '') return JSON.parse(this.getRawAnswer());

    return this.getRawAnswer();
  }

  getOptions(_) {
    return this.state.options || [];
  }

  renderOptions() {
    if (this.state.loading) {
      return null; // TODO
    }
  
    const selected = this.getSelected();
    const options = this.getOptions();

   return <>
          {Object.keys(selected).map(colAccessor => {
            return <input 
              key={this.fullFieldName() + colAccessor} 
              type="hidden" 
              name={this.fullFieldName() + '[' + colAccessor + ']'} 
              value={typeof selected[colAccessor] == 'object' ? JSON.stringify(selected[colAccessor]) : selected[colAccessor]} 
            />
          })}          
          <Select
            value={selected}
            autoComplete={this.getAutoComplete()}
            readOnly={this.props.mode != Mode.FILL}
            disabled={(this.props.mode == Mode.FILL && this.isReadOnlyAnswer()) || this.props.disabled}
            onChange={this.onChange}
            className={`${this.isReadOnlyAnswer() ? 'locked' : ''}`}
            options={options}
            getSelectedOptions={getSelectedOption}
            valueAccessor={(selectedOptions, isMulti) => selectedOptions}
            autoComplete={this.getAutoComplete()}
          />
    </>
  }


  // Overwrite the trigger to target the ID field
  triggerComponentClickedEvent = (event) => {
    if (typeof this.props.triggerEvent !== 'function') return;

    this.props.triggerEvent('componentClicked', {content: this.props.content, event, fullFieldName: this.fullFieldName() + '[id]'})
  }


  onChange = (value) => {
    const projectId = this.context.projectId;
    const { datagridId } = this.props.content;


    fetchJson(datagrid_get_row_path({id: datagridId, row_id: value, project_id: projectId, _options: true}))
      .then(
      (res) => {
        this.setState((prevState) => {
          // Intentionally mutate. The updateAnswer will handle refreshing
          // Update the loaded option as a value. Since by default we don't load all options
          const optionIndex = prevState.options.findIndex(opt => opt.value.id == res.id)

          return {options: prevState.options}
        })

        const selectedOption = this.state.options.find(opt => opt.value.id == res.id)

        // Store the option to show in display mode to not do any fetching.
        res.selectedOption = selectedOption.label;

        // Set the whole answer as value
        this.updateAnswer(res);
      }
    )
  };

  renderView() {
    return (
        <div>
            {this.getLockedInformationTooltip(this.modeFillOrView() && this.isReadOnlyPrefilledAnswer(), <>
                <label className={this.props.content.label_hidden ? 'hidden-label': ''}>
                    <span className="field-label">{this.getText('label')}</span>
                    {this.props.content.required ? <span className='required-field'> *</span> : null}
                    {this.helpIcon()}
                    {this.renderPrefillIcon()}
                </label>
                {this.renderOptions()}
                {this.descriptionText()}
                {!this.modeFillOrView() && this.isReadOnlyAnswer() ? this.lockedInformationDefault() : ''}
            </>)}
        </div>
    );
  }

  renderSettings() {
    return <div>
      {this.renderDatagridPresetSelection({
        description: I18n.t('datagrid_row_lookup_preset_selection_description', {scope: 'fe'}),
        presetFilter: nonGroupedFilter
      })}
      {this.renderDatagridColumnSelection(this.props.content.datagridId)}
      <Setting.TextField name='label' label={I18n.t('fe')['field_label']} value={this.getText('label')} onChange={this.updateSettings} />
      <Setting.Checkbox name='label_hidden' className='label-hidden-check' label={I18n.t('fe')['field_label_hide']} value={this.props.content.label_hidden} onChange={this.updateSettings} />
      <Setting.LongTextField name='description' label={I18n.t('fe')['field_description']} value={this.getText('description')} onChange={this.updateSettings} />
      <Setting.LongTextField name='help' label={I18n.t('fe')['field_help']} value={this.getText('help')} onChange={this.updateSettings} />
      <Setting.Checkbox name='includeBlank' label={I18n.t('fe')['field_include_blank']} value={this.props.content.includeBlank} onChange={this.updateSettings} />
      <Setting.TextField name='blankOptionLabel' label={I18n.t('fe')['select_blank_option_label']} value={this.getText('blankOptionLabel')} onChange={this.updateSettings} />
      <Setting.TextField
        name='reportKey'
        label={I18n.t('fe')['field_report_key']}
        value={this.props.content.reportKey}
        description={I18n.t('fe')['field_report_key_description']}
        {...(this.allowModifyInSafeMode ? {onChange: this.updateSettings} : {disabled: true})}
      />
    </div>
  }
}

const fetchOptionsForSelect = (project_id, datagrid_id, preset_id, columnAccessor, cb) => {
  fetchJson(
    datagrid_get_cells_for_column_path({
      id: datagrid_id,
      datagrid_col_id: columnAccessor,
      preset_id: preset_id,
      project_id: project_id,
      _options: true
  })).then(
    (res) => {
      cb(res.map(p => ({ ...p })));
    }
  )
};